/**
 * `UnitMatrix` — one row per unit, one column per constraint, one small square per pair:
 * filled in the constraint's own colour when the unit is flagged, neutral when it is not,
 * hatched when the constraint could not be assessed there. A dot in the unit's priority
 * colour sits beside its name, so "which places carry which constraints" and "which places
 * are High Priority" read off one picture (2 Oct chart round, design §1.2).
 *
 * Hand-built SVG in the same register as the other charts in this folder (the repo forbids
 * chart libraries): viewBox-scaled, `role="img"` with a full plain-text readout naming each
 * unit's flagged and not-assessed constraints.
 *
 * The column headers are the short codes C1…C5 rather than the constraint names — five
 * names across the dock's width would be three characters each — and each one is a real
 * button: `role="button"`, in the tab order, Enter and Space, and an `aria-label` that is
 * the constraint's full name rather than its code. The legend under the chart spells each
 * code out too, so the codes are never unexplained on screen either.
 */
import type { KeyboardEvent } from 'react';

import { NEUTRAL_COLOR } from '../uc1Constants';
import { clipLabel } from './labels';

export const UM_W = 394;
/** One column of the grid — the cell plus the air around it, header and cells alike. */
export const UM_COL_W = 30;
export const UM_CELL = 18;
/** Row pitch, `DimensionBars`' own, so a matrix and a bar chart read at the same rhythm. */
export const UM_ROW_H = 22;
/** Header band: the C1…C5 codes. */
export const UM_HEADER_H = 20;
export const UM_PAD_TOP = 4;
export const UM_MIN_H = 48;

/** The hatch a not-assessed cell is filled with — one `<pattern>` per rendered chart. */
const HATCH_ID = 'unit-matrix-hatch';
/** "Not flagged", and the ground a hatch is drawn over — `uc1Constants`' own mid grey. */
// The one neutral grey, from `uc1Constants.ts`. Copied by hand until 3 Oct 2026, when
// the client had it darkened and every copy went stale in a different place.
const NEUTRAL = NEUTRAL_COLOR;

export interface UnitMatrixUnit {
  id: string;
  name: string;
  /** Priority class id — only used to pick the dot's colour and word it in the readout. */
  category: string;
}

export interface UnitMatrixColumn {
  id: string;
  /** The constraint's full name, for the legend, the tooltip and the readout. */
  label: string;
  /** The code printed in the header — `C1`…`C5`. */
  short: string;
  color: string;
}

export interface UnitMatrixProps {
  units: UnitMatrixUnit[];
  columns: UnitMatrixColumn[];
  /** `true` flagged, `false` not flagged, `null`/`undefined` not assessed. */
  flagOf: (unitId: string, columnId: string) => boolean | null | undefined;
  categoryColors: Record<string, string>;
  categoryLabel: (category: string) => string;
  /** The three states in words — the readout and the tooltips print these. */
  statusLabels: { flagged: string; notFlagged: string; notAssessed: string };
  ariaLabel: string;
  onSelectColumn?: (columnId: string) => void;
  selectedColumn?: string | null;
  /**
   * The unit the pane is focused on (`#intelFocus`, or a map click). Its name is drawn
   * bold so the row a reader has asked about is findable in a matrix of eleven; nothing
   * else about the row changes, because the cells already carry its answers.
   */
  selectedId?: string | null;
}

/** The chart's own `aria-label`: per unit, its priority and which constraints it carries. */
function readout(
  ariaLabel: string,
  units: UnitMatrixUnit[],
  columns: UnitMatrixColumn[],
  flagOf: UnitMatrixProps['flagOf'],
  categoryLabel: UnitMatrixProps['categoryLabel'],
  statusLabels: UnitMatrixProps['statusLabels'],
): string {
  const parts = units.map((unit) => {
    const flagged: string[] = [];
    const missing: string[] = [];
    for (const column of columns) {
      const flag = flagOf(unit.id, column.id);
      if (flag === true) flagged.push(column.label);
      else if (flag == null) missing.push(column.label);
    }
    const tail = [
      `${statusLabels.flagged}: ${flagged.length ? flagged.join(', ') : '—'}`,
      ...(missing.length ? [`${statusLabels.notAssessed}: ${missing.join(', ')}`] : []),
    ].join('; ');
    return `${unit.name}, ${categoryLabel(unit.category)} — ${tail}`;
  });
  return `${ariaLabel} — ${parts.join('. ')}`;
}

export function UnitMatrix({
  units,
  columns,
  flagOf,
  categoryColors,
  categoryLabel,
  statusLabels,
  selectedId = null,
  ariaLabel,
  onSelectColumn,
  selectedColumn,
}: UnitMatrixProps) {
  const h = Math.max(
    UM_MIN_H,
    UM_PAD_TOP + UM_HEADER_H + Math.max(units.length, 1) * UM_ROW_H + 2,
  );
  /**
   * The grid is right-aligned on the chart's own edge and every column is one width, so
   * the header code, the cells under it and the next chart's value column all sit on the
   * same verticals (layout round, 2 Oct 2026). What is left is the label column.
   */
  const gridX = UM_W - columns.length * UM_COL_W;
  /** The centre of column `i` — the one number the header and its cells both read. */
  const colCx = (i: number) => gridX + i * UM_COL_W + UM_COL_W / 2;
  const colX = (i: number) => colCx(i) - UM_CELL / 2;
  /** Name column: what is left of the grid, less the priority dot and its gap. */
  const labelChars = Math.floor((gridX - 20) / 5.3);

  return (
    <svg
      className="intel-chart unit-matrix"
      viewBox={`0 0 ${UM_W} ${h}`}
      role="img"
      aria-label={readout(ariaLabel, units, columns, flagOf, categoryLabel, statusLabels)}
      data-testid="unit-matrix-chart"
    >
      <defs>
        <pattern id={HATCH_ID} width={4} height={4} patternUnits="userSpaceOnUse" patternTransform="rotate(45)">
          <rect width={4} height={4} fill={NEUTRAL} />
          <line x1={0} y1={0} x2={0} y2={4} stroke="var(--ink-3)" strokeWidth={1.2} />
        </pattern>
      </defs>

      {columns.map((column, i) => {
        const on = column.id === selectedColumn;
        const press = onSelectColumn ? () => onSelectColumn(column.id) : undefined;
        const onKeyDown = press
          ? (e: KeyboardEvent<SVGGElement>) => {
              if (e.key !== 'Enter' && e.key !== ' ' && e.key !== 'Spacebar') return;
              e.preventDefault();
              press();
            }
          : undefined;
        return (
          <g
            key={column.id}
            className={on ? 'um-col on' : 'um-col'}
            data-col={column.id}
            data-testid={`unit-matrix-col-${column.id}`}
            style={press ? { cursor: 'pointer' } : undefined}
            {...(press
              ? {
                  role: 'button',
                  tabIndex: 0,
                  'aria-label': column.label,
                  'aria-pressed': on,
                  onKeyDown,
                }
              : {})}
            onClick={press}
          >
            <title>{column.label}</title>
            <rect
              x={colCx(i) - UM_COL_W / 2}
              y={UM_PAD_TOP}
              width={UM_COL_W}
              height={UM_HEADER_H - 2}
              rx={3}
              fill={on ? column.color : 'transparent'}
              opacity={on ? 0.18 : 1}
            />
            <text
              className="um-colhead"
              x={colCx(i)}
              y={UM_PAD_TOP + UM_HEADER_H - 7}
              textAnchor="middle"
              fill={column.color}
            >
              {column.short}
            </text>
          </g>
        );
      })}

      {units.map((unit, r) => {
        const y = UM_PAD_TOP + UM_HEADER_H + r * UM_ROW_H;
        return (
          <g key={unit.id} className="um-row" data-row={unit.id}>
            <circle
              cx={5}
              cy={y + UM_CELL / 2}
              r={4}
              fill={categoryColors[unit.category] ?? NEUTRAL}
              data-testid={`unit-matrix-dot-${unit.id}`}
            >
              <title>{categoryLabel(unit.category)}</title>
            </circle>
            <text
              className="um-rowlabel"
              x={14}
              y={y + UM_CELL / 2 + 3.5}
              fontWeight={unit.id === selectedId ? 700 : undefined}
            >
              {clipLabel(unit.name, labelChars)}
            </text>
            {columns.map((column, i) => {
              const flag = flagOf(unit.id, column.id);
              const state = flag === true ? 'flagged' : flag === false ? 'notFlagged' : 'notAssessed';
              const fill =
                state === 'flagged'
                  ? column.color
                  : state === 'notFlagged'
                    ? NEUTRAL
                    : `url(#${HATCH_ID})`;
              return (
                <rect
                  key={column.id}
                  className={`um-cell um-${state}`}
                  data-cell={`${unit.id}:${column.id}`}
                  data-state={state}
                  x={colX(i)}
                  y={y}
                  width={UM_CELL}
                  height={UM_CELL}
                  rx={3}
                  fill={fill}
                >
                  <title>{`${unit.name} · ${column.label} · ${statusLabels[state]}`}</title>
                </rect>
              );
            })}
          </g>
        );
      })}
    </svg>
  );
}

export default UnitMatrix;
